import type { TFunction } from "i18next";
import type { FusionItemDef, FusionMenuConfig } from "~/components/dock/FusionMenu";

import { ApplicationEnum } from "~/@types/enums";
import { LearningModule } from "~/gql/graphqlBase.generated";
import { isTenantSettingsPageId } from "~/helpers/canOpenTenantSettings";

const createProduceFusionConfig = (
	t: TFunction<"common">,
	isPlanBuilderEnabled: boolean,
	consolidationEnabled: boolean,
	canViewCostReport: boolean,
	canManageSettings = false,
	productionCenterEnabled = false
): FusionMenuConfig => ({
	defaultQuickActionKeys: [
		"purchase-orders",
		"recipes?action=new",
		"production-items",
		"vendors?action=new",
		"production-schedule",
		"hardgoods"
	],
	learningModule: LearningModule.Produce,
	logo: "/logos/produce.svg",
	module: ApplicationEnum.PRODUCE,
	quickActions: [
		{
			icon: "receipt_long",
			label: t("dock.quickActionLabels.produce.newPurchaseOrder"),
			pageId: "purchase-orders"
		},
		{ action: "new", icon: "menu_book", label: t("dock.quickActionLabels.produce.newRecipe"), pageId: "recipes" },
		{
			icon: "category",
			label: t("dock.quickActionLabels.produce.newProductionItem"),
			pageId: "production-items"
		},
		{ action: "new", icon: "storefront", label: t("dock.quickActionLabels.produce.newVendor"), pageId: "vendors" },
		{
			icon: productionCenterEnabled ? "view_quilt" : "calendar_month",
			label: productionCenterEnabled
				? t("produce:productionCenter.title")
				: t("dock.quickActionLabels.produce.productionSchedule"),
			pageId: "production-schedule"
		},
		{
			icon: "inventory_2",
			label: t("dock.quickActionLabels.produce.hardGoodsInventory"),
			pageId: "hardgoods"
		},
		{
			icon: "engineering",
			label: t("dock.quickActionLabels.produce.laborSchedule"),
			pageId: "labor-schedule"
		},
		{ icon: "monitoring", label: t("dock.quickActionLabels.produce.vendorSpend"), pageId: "vendor-spend" }
	],
	sections: [
		{
			groups: [
				{
					items: [
						{ icon: "dashboard", pageId: "dashboard" },
						{ icon: "calendar_view_week", pageId: "production-calendar" }
					]
				}
			],
			icon: "dashboard",
			id: "dashboard",
			label: t("dock.pages.produce.dashboard")
		},
		{
			groups: [
				{
					items: [
						...(isPlanBuilderEnabled ? [{ icon: "edit_calendar", pageId: "plan-builder" }] : []),
						{ icon: productionCenterEnabled ? "view_quilt" : "calendar_month", pageId: "production-schedule" },
						{ icon: "event_note", pageId: "event-schedule" }
					],
					label: t("dock.pages.produce.groupSchedule")
				},
				{
					items: [
						{ icon: "menu_book", pageId: "recipes" },
						{ icon: "link", pageId: "recipes-link-health" },
						{ icon: "category", pageId: "production-items" },
						{ icon: "package_2", pageId: "material-items" },
						{ icon: "bolt", pageId: "events" },
						{ icon: "alt_route", pageId: "triggers" }
					],
					label: t("dock.pages.produce.groupSetup")
				},
				{
					items: [
						...(canViewCostReport ? [{ icon: "assessment", pageId: "cost-report" }] : []),
						{ icon: "trending_down", pageId: "shrink-loss" }
					],
					label: t("dock.pages.produce.groupReporting")
				}
			],
			icon: "potted_plant",
			id: "production",
			label: t("dock.pages.produce.sectionProduction")
		},
		{
			groups: [
				{
					items: [
						{ icon: "calendar_month", pageId: "labor-schedule" },
						{ icon: "groups", pageId: "labor-crew" },
						{ icon: "payments", pageId: "labor-rates" },
						{ icon: "checklist", pageId: "labor-tasks" }
					]
				}
			],
			icon: "engineering",
			id: "labor",
			label: t("dock.pages.produce.sectionLabor")
		},
		{
			groups: [
				{
					items: [
						{ icon: "receipt_long", pageId: "purchase-orders" },
						{ icon: "monitoring", pageId: "vendor-spend" },
						{ icon: "storefront", pageId: "vendors" }
					]
				}
			],
			icon: "shopping_cart",
			id: "purchasing",
			label: t("dock.pages.produce.sectionPurchasing")
		},
		{
			groups: [{ items: [{ icon: "inventory_2", pageId: "hardgoods" }] }],
			icon: "inventory_2",
			id: "inventory",
			label: t("dock.pages.produce.sectionInventory")
		},
		{
			groups: [
				{
					items: [
						{ icon: "category", pageId: "categories-and-types" },
						{ icon: "location_on", pageId: "capacity-locations" },
						{ icon: "percent", pageId: "overhead-rates" },
						{ icon: "space_dashboard", pageId: "space-planning" }
					],
					label: t("dock.pages.produce.groupCapacity")
				},
				{
					items: [{ icon: "swap_horiz", pageId: "data-transform" }],
					label: t("dock.pages.produce.groupTools")
				}
			],
			icon: "space_dashboard",
			id: "capacity",
			label: t("dock.pages.produce.sectionCapacity")
		},
		{
			groups: [
				{
					items: [
						{ icon: "assessment", label: t("dock.pages.reports.allReports"), path: "/reports" },
						{ icon: "menu_book", label: t("dock.pages.reports.templates"), path: "/reports/templates" },
						{ icon: "schedule", label: t("dock.pages.reports.schedules"), path: "/reports/schedules" },
						{ icon: "history", label: t("dock.pages.reports.executionHistory"), path: "/reports/executions" }
					]
				}
			],
			icon: "assessment",
			id: "reports",
			label: t("dock.pages.reports.section")
		},
		{
			groups: [
				{
					items: [
						{ icon: "tune", pageId: "settings" },
						...(consolidationEnabled ? [{ icon: "merge", pageId: "settings-consolidation" }] : [])
					]
				}
			],
			icon: "settings",
			id: "settings",
			label: t("dock.pages.produce.sectionSettings")
		}
	]
		.map((section) => ({
			...section,
			groups: section.groups.map((group) => ({
				...group,
				items: group.items.filter((item: FusionItemDef) => canManageSettings || !isTenantSettingsPageId(item.pageId))
			}))
		}))
		.filter((section) => section.groups.some((group) => group.items.length > 0))
});

export default createProduceFusionConfig;
